import { Col, Form as BSForm, Row, Button } from 'react-bootstrap';
import { useState, useEffect, useCallback } from 'react';
import { Field, useFormikContext } from 'formik';
import ReactDatePicker from 'react-datepicker';
import _ from 'lodash';
import { debounce } from 'lodash';
import SigniteTextArea from '../../../../ui-elements/signite-text-area/signiteTextArea';
import { getErrorClass, getErrorMessage } from '../../../../../utils/errorUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { ClientService } from '../../../../../services/clientService';
import { MasterDataService } from '../../../../../services/masterDataService';

export const CurrentRoleForm = ({ handleChange, handleBlur, values, errors, touched, initialized, cities, industrySectors, functions, moveCurrentRoleToPrevious }) => {
  const { setFieldValue } = useFormikContext();
  const [selectedIndustryId, setSelectedIndustryId] = useState(null);
  const [industrySubcategories, setIndustrySubcategories] = useState([]);
  const [orgSuggestions, setOrgSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [functionSubcategories, setFunctionSubcategories] = useState([]);
  const [selectedFunctionId, setSelectedFunctionId] = useState(null);

  const fetchOrganizations = useCallback(
    debounce(async (prefix) => {
      if (!prefix || prefix.trim().length === 0) {
        setOrgSuggestions([]);
        setShowDropdown(false);
        return;
      }

      try {
        const res = await ClientService.findMatching(prefix);

        const suggestions = res.data || [];

        setOrgSuggestions(suggestions);
        setShowDropdown(suggestions.length > 0);
      } catch (err) {
        console.error('Error fetching org suggestions:', err);
      }
    }, 100),
    []
  );


  const handleOrgChange = (e) => {
    handleChange(e);
    setFieldValue('currentOrganizationId', '');
    fetchOrganizations(e.target.value);
  };


  const handleSelectSuggestion = (org) => {
    setFieldValue('currentOrganizationName', org.companyName);
    setFieldValue('currentOrganizationId', org.clientId);
    setOrgSuggestions([]);
    setShowDropdown(false);
  };

  useEffect(() => {
    if (!industrySectors || industrySectors.length === 0) return;

    if (values.currentIndustrySector) {
      const match = industrySectors.find(
        (s) => s.industrySector?.toLowerCase() === (values.currentIndustrySector || '').toLowerCase()
      );
      if (match) {
        const id = match.id;
        setSelectedIndustryId(id);
        MasterDataService.getIndustrySubcategories(id)
          .then((res) => setIndustrySubcategories(res.data || []))
          .catch((err) => console.error('Failed to load subcategories:', err));
      }
    }
  }, [industrySectors, values.currentIndustrySector]);

  const onIndustrySelect = async (e) => {
    const id = e.target.value || null;
    setSelectedIndustryId(id);

    if (!id) {
      setFieldValue('currentIndustrySector', '');
      setFieldValue('currentIndustrySubCategory', '');
      setIndustrySubcategories([]);
      return;
    }

    const match = industrySectors.find((s) => s.id == id);
    const label = match?.industrySector || '';
    setFieldValue('currentIndustrySector', label);
    setFieldValue('currentIndustrySubCategory', '');

    try {
      const res = await MasterDataService.getIndustrySubcategories(id);
      setIndustrySubcategories(res.data || []);
    } catch (err) {
      console.error('Failed to load subcategories:', err);
      setIndustrySubcategories([]);
    }
  };

  useEffect(() => {
    if (!functions || functions.length === 0) return;

    if (values.currentFunction) {
      const match = functions.find(
        (f) => f.function.toLowerCase() === values.currentFunction.toLowerCase()
      );

      if (match) {
        setSelectedFunctionId(match.id);

        MasterDataService.getFunctionSubcategories(match.id)
          .then((res) => setFunctionSubcategories(res.data || []))
          .catch(() => setFunctionSubcategories([]));
      }
    }
  }, [functions, values.currentFunction]);

  const onFunctionSelect = async (e) => {
    const id = e.target.value;
    setSelectedFunctionId(id);

    if (!id) {
      setFieldValue("currentFunction", "");
      setFieldValue("currentFunctionSubCategory", "");
      setFunctionSubcategories([]);
      return;
    }

    const match = functions.find((f) => f.id == id);
    const label = match?.function || "";

    setFieldValue("currentFunction", label);
    setFieldValue("currentFunctionSubCategory", "");

    try {
      const res = await MasterDataService.getFunctionSubcategories(id);
      setFunctionSubcategories(res.data || []);
    } catch (err) {
      setFunctionSubcategories([]);
    }
  };



  return (
    <div className="signite-form">
      <div className="flex items-center justify-space-between mb-4 w-full">
        <p style={{ fontSize: '18px', fontWeight: '600', color: '#2d3e50' }}>
          Current Role &nbsp;&nbsp;&nbsp;
          {values.currentDesignation?.trim() || values.currentOrganizationName?.trim() ? (
            <button
              type="button"
              onClick={() => {
                moveCurrentRoleToPrevious(values, setFieldValue);
                setSelectedIndustryId(null);
                setIndustrySubcategories([]);

                setSelectedFunctionId(null);
                setFunctionSubcategories([]);
              }}
              className="py-2 whitespace-nowrap inline-flex"
              style={{
                fontSize: '14px',
                fontWeight: '600',
                background: 'none',
                color: '#F35824',
                borderRadius: '100px',
                boxShadow: 'none',
                border: 'none',
              }}
            >
              <FontAwesomeIcon icon={faPlusCircle} />{' '}
              Add New Current Role
            </button>
          ) : null}
        </p>
      </div>

      <Row className="mb-3">
        <BSForm.Group as={Col}>
          <BSForm.Label>Designation*</BSForm.Label>
          <BSForm.Control
            type="text"
            name="currentDesignation"
            onChange={handleChange}
            onBlur={handleBlur}
            className={`form-control ${getErrorClass(errors, touched, 'currentDesignation', initialized)}`}
            value={values.currentDesignation}
            required
          />
          <span className="errorMessage">{getErrorMessage(errors, touched, 'currentDesignation', initialized)}</span>
        </BSForm.Group>

        <BSForm.Group as={Col} style={{ position: 'relative' }}>
          <BSForm.Label>Organization Name*</BSForm.Label>
          <BSForm.Control
            type="text"
            name="currentOrganizationName"
            autoComplete="off"
            onChange={handleOrgChange}
            onBlur={handleBlur}
            value={values.currentOrganizationName}
            className={`form-control ${getErrorClass(errors, touched, 'currentOrganizationName', initialized)}`}
            required
          />
          {showDropdown &&
            orgSuggestions.length > 0 &&
            values.currentOrganizationName.trim().length > 0 && (
              <ul
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1050,
                  background: 'white',
                  border: '1px solid #ccc',
                  borderTop: 'none',
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  maxHeight: '200px',
                  overflowY: 'auto',
                }}
              >
                {orgSuggestions.map((org) => (
                  <li
                    key={org.clientId}
                    style={{ padding: '8px', cursor: 'pointer' }}
                    onMouseDown={() => handleSelectSuggestion(org)}
                  >
                    {org.companyName}
                  </li>
                ))}
              </ul>
            )}


          <span className="errorMessage">
            {getErrorMessage(errors, touched, 'currentOrganizationName', initialized)}
          </span>
        </BSForm.Group>



        {/* <BSForm.Group as={Col}>
          <BSForm.Label>Industry/Sector*</BSForm.Label>
          <BSForm.Control
            type="text"
            name="currentIndustrySector"
            onChange={handleChange}
            onBlur={handleBlur}
            className={`form-control ${getErrorClass(errors, touched, 'currentIndustrySector', initialized)}`}
            value={values.currentIndustrySector}
            required
          />
          <span className="errorMessage">{getErrorMessage(errors, touched, 'currentIndustrySector', initialized)}</span>
        </BSForm.Group> */}
        <BSForm.Group as={Col}>
          <BSForm.Label>Industry / Sector*</BSForm.Label>

          <BSForm.Select
            name="currentIndustrySector_select"
            value={selectedIndustryId || ''}
            onChange={onIndustrySelect}
            className={`form-control ${getErrorClass(errors, touched, 'currentIndustrySector', initialized)}`}
            required
          >
            <option value="">Select Industry</option>
            {industrySectors.map((sector) => (
              <option key={sector.id} value={sector.id}>
                {sector.industrySector}
              </option>
            ))}
          </BSForm.Select>

          <span className="errorMessage">{getErrorMessage(errors, touched, 'currentIndustrySector', initialized)}</span>
        </BSForm.Group>
      </Row>
      <Row className="mb-3">
        <BSForm.Group as={Col}>
          {industrySubcategories && industrySubcategories.length > 0 ? (
            <>
              <BSForm.Label>Industry Subcategory</BSForm.Label>
              <BSForm.Select
                name="currentIndustrySubCategory"
                value={values.currentIndustrySubCategory || ''}
                onChange={(e) => setFieldValue('currentIndustrySubCategory', e.target.value)}
                className="form-control"
              >
                <option value="">Select Subcategory</option>
                {industrySubcategories.map((sub) => (
                  <option key={sub.id} value={sub.subcategory}>
                    {sub.subcategory}
                  </option>
                ))}
              </BSForm.Select>
              <span className="errorMessage">{getErrorMessage(errors, touched, 'currentIndustrySubCategory', initialized)}</span>
            </>
          ) : (
            <>
              <BSForm.Label>Industry Subcategory</BSForm.Label>
              <BSForm.Control
                type="text"
                name="currentIndustrySubCategory"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.currentIndustrySubCategory || ''}
                placeholder="Select industry to load subcategories"
                disabled
              />
            </>
          )}
        </BSForm.Group>

        <BSForm.Group as={Col}>
          <BSForm.Label>Function</BSForm.Label>
          <BSForm.Select
            name="currentFunction_select"
            value={selectedFunctionId || ""}
            onChange={onFunctionSelect}
          >
            <option value="">Select Function</option>
            {functions.map((fn) => (
              <option key={fn.id} value={fn.id}>
                {fn.function}
              </option>
            ))}
          </BSForm.Select>
        </BSForm.Group>

        <BSForm.Group as={Col}>
          <BSForm.Label>Function Subcategory</BSForm.Label>

          {functionSubcategories.length > 0 ? (
            <BSForm.Select
              name="currentFunctionSubCategory"
              value={values.currentFunctionSubCategory || ""}
              onChange={(e) => setFieldValue("currentFunctionSubCategory", e.target.value)}
            >
              <option value="">Select Subcategory</option>
              {functionSubcategories.map((sub) => (
                <option key={sub.id} value={sub.subcategory}>
                  {sub.subcategory}
                </option>
              ))}
            </BSForm.Select>
          ) : (
            <BSForm.Control
              type="text"
              disabled
              placeholder="Select function first"
            />
          )}
        </BSForm.Group>

        <BSForm.Group as={Col}>
          <BSForm.Label>Location</BSForm.Label>
          <BSForm.Select
            name="currentLocation"
            value={values.currentLocation}
            onChange={(e) => setFieldValue('currentLocation', e.target.value)}
            onBlur={handleBlur}
            className={`form-control ${getErrorClass(errors, touched, 'currentLocation', initialized)}`}
          >
            <option value="">Select a city</option>
            {cities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </BSForm.Select>
          <span className="errorMessage">
            {getErrorMessage(errors, touched, 'currentLocation', initialized)}
          </span>
        </BSForm.Group>
        <BSForm.Group as={Col}>
          <BSForm.Label>Start Date MM/YYYY</BSForm.Label>
          <ReactDatePicker
            name={'currentRoleStartDate'}
            selected={values.currentRoleStartDate ? new Date(values.currentRoleStartDate) : null}
            onChange={(val) => {
              setFieldValue('currentRoleStartDate', val, true);
            }}
            onBlur={handleBlur}
            className={`form-control ${getErrorClass(errors, touched, 'currentRoleStartDate', initialized)}`}
            dateFormat="MM/yyyy"
            maxDate={new Date()}
            showMonthYearPicker
            autoComplete="off"
          />
          <span className="errorMessage">{getErrorMessage(errors, touched, 'currentRoleStartDate', initialized)}</span>
        </BSForm.Group>
        <BSForm.Group as={Col}>
          <BSForm.Label>Notice Period (days)</BSForm.Label>
          <BSForm.Control
            type="text"
            name="currentNoticePeriod"
            onChange={handleChange}
            onBlur={handleBlur}
            className={`form-control ${getErrorClass(errors, touched, 'currentNoticePeriod', initialized)}`}
            value={values.currentNoticePeriod}
            required
          />
          <span className="errorMessage">{getErrorMessage(errors, touched, 'currentNoticePeriod', initialized)}</span>
        </BSForm.Group>
      </Row>
      <Row>
        <BSForm.Group as={Col}></BSForm.Group>
        <BSForm.Group as={Col}></BSForm.Group>
        <BSForm.Group as={Col}></BSForm.Group>
      </Row>
      <Row>
        <BSForm.Group as={Col}>
          <BSForm.Label>My Top 3 Responsibilities are</BSForm.Label>
          <SigniteTextArea
            name={'currenttop3Responsibilities'}
            value={values.currenttop3Responsibilities}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`ta-wrapper char-count ${getErrorClass(
              errors,
              touched,
              'currenttop3Responsibilities',
              initialized
            )}`}
          ></SigniteTextArea>
          <span className="errorMessage">
            {getErrorMessage(errors, touched, 'currenttop3Responsibilities', initialized)}
          </span>
        </BSForm.Group>
      </Row>
      <Row>
        <BSForm.Group as={Col}>
          <BSForm.Label>My Top 3 Achievements are</BSForm.Label>
          <SigniteTextArea
            name={'currenttop3Achievements'}
            value={values.currenttop3Achievements}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`ta-wrapper char-count ${getErrorClass(
              errors,
              touched,
              'currenttop3Achievements',
              initialized
            )}`}
          ></SigniteTextArea>
          <span className="errorMessage">
            {getErrorMessage(errors, touched, 'currenttop3Achievements', initialized)}
          </span>
        </BSForm.Group>
      </Row>
    </div>
  );
};
